import { createFileRoute } from "@tanstack/react-router";
import { Breadcrumbs, PageHeader, Section, SectionHeading } from "@/components/site/section";
import { CtaLink } from "@/components/site/cta";
import { FinalCTA } from "@/components/site/final-cta";
import { pageUrl } from "@/data/site";

export const Route = createFileRoute("/bottle-stopper-blanks")({
  head: () => ({
    meta: [
      { title: "Bottle Stopper Blanks | Paul's Pens n Things" },
      {
        name: "description",
        content:
          "Cast bottle stopper blanks with embedded porcelain roses, paper roses and fishing flies. 1 1/2 x 2 1/2 inch polyester resin blanks from $14.95.",
      },
      { property: "og:title", content: "Bottle stopper blanks for penturners" },
      {
        property: "og:description",
        content:
          "Porcelain roses, paper roses and fishing flies cast into clear-topped bottle stopper blanks over a solid color base.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: pageUrl("/bottle-stopper-blanks") },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: pageUrl("/bottle-stopper-blanks") }],
  }),
  component: BottleStopperBlanks,
});

type Blank = {
  sku: string;
  base: string;
  price: string;
  spec: string;
  image: string;
  alt: string;
};

const SHIPPING = "Shipping is $6 for 1 to 5, $11 for 6 to 25, and $19 for 26 or more.";
const SIZE =
  "Approximately 1 1/2 inches in diameter by 2 1/2 inches long. Made with a polyester resin.";

const bases = ["Red", "Blue", "Green", "Gold", "Silver"];

function build(
  prefix: string,
  detail: (base: string) => string,
  price: string,
  altInset: string,
  skus?: string[],
): Blank[] {
  const list = skus ?? bases.map((b) => `${prefix}-${b.toLowerCase()}`);
  return list.map((sku, i) => ({
    sku,
    base: bases[i],
    price,
    spec: `${detail(bases[i].toLowerCase())} ${SIZE}`,
    image: `/${sku}.webp`,
    alt: `Bottle stopper blank with a ${altInset} over a ${bases[i].toLowerCase()} base`,
  }));
}

const largePink = build(
  "bslppr",
  (b) => `The base is ${b} and the pink porcelain rose is about 1 inch in diameter.`,
  "$14.95",
  "large pink porcelain rose",
  ["bslppr-red", "bslppr-blue", "bslppr-green", "bslppr-gold", "bslppr-silver"],
);

const smallBlue = build(
  "bssbpr",
  (b) => `The base is ${b} and the blue porcelain rose is about 5/8 inch in diameter.`,
  "$14.95",
  "smaller blue porcelain rose",
);

const redPaper = build(
  "bserpr",
  (b) => `The base is ${b} and the red paper rose is about 5/8 inch in diameter.`,
  "$14.95",
  "red paper rose",
);

const whitePaper = build(
  "bsewpr",
  (b) => `The base is ${b} and the white paper rose is about 5/8 inch in diameter.`,
  "$14.95",
  "white paper rose",
  ["bsewpr-red", "bsewpr-blue", "bsewpr-green", "bsewpr-gold"],
);

const embeddedFly: Blank[] = ["Red", "Blue", "Gold", "Silver"].map((base) => ({
  sku: `bseff-${base.toLowerCase()}`,
  base,
  price: "$14.95",
  spec: `The base is ${base.toLowerCase()} and the fishing fly is embedded at the bottom of the upper portion. ${SIZE}`,
  image: `/bseff-${base.toLowerCase()}.webp`,
  alt: `Bottle stopper blank with an embedded fishing fly over a ${base.toLowerCase()} base`,
}));

const suspendedFly: Blank[] = ["Green", "Gold", "Silver"].map((base) => ({
  sku: `bssff-${base.toLowerCase()}`,
  base,
  price: "$16.95",
  spec: `The base is ${base.toLowerCase()} and the fishing fly is suspended in the upper portion. ${SIZE}`,
  image: `/bssff-${base.toLowerCase()}.webp`,
  alt: `Bottle stopper blank with a suspended fishing fly over a ${base.toLowerCase()} base`,
}));

function BlankGrid({ items }: { items: Blank[] }) {
  return (
    <ul className="mt-12 grid gap-px border border-hairline bg-hairline sm:grid-cols-2 lg:grid-cols-3">
      {items.map((b) => (
        <li key={b.sku} className="flex flex-col bg-background">
          <div className="overflow-hidden bg-paper">
            <img
              src={b.image}
              alt={b.alt}
              loading="lazy"
              width={360}
              height={480}
              className="h-full w-full object-cover motion-craft transition-transform duration-700 hover:scale-[1.03]"
            />
          </div>
          <div className="flex flex-1 flex-col p-7 sm:p-8">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              {b.base} base
            </p>
            <h3 className="mt-2 font-[family-name:var(--font-display)] text-2xl leading-tight">
              {b.sku}
            </h3>
            <p className="mt-2 text-lg font-semibold text-walnut">{b.price}</p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{b.spec}</p>
            <div className="mt-auto pt-6">
              <CtaLink to="/contact" variant="outline" className="h-11">
                Order this blank
              </CtaLink>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function BottleStopperBlanks() {
  return (
    <>
      <Breadcrumbs
        trail={[
          { label: "Home", to: "/" },
          { label: "For Penturners", to: "/for-pen-turners" },
          { label: "Bottle Stopper Blanks" },
        ]}
      />
      <PageHeader
        eyebrow="Products for penturners"
        title="Bottle stopper blanks."
        lede="Porcelain roses, paper roses and hand-tied fishing flies cast into a clear upper portion over a solid color base. Every blank is roughly 1 1/2 inches in diameter by 2 1/2 inches long in polyester resin, ready for a standard stopper mandrel."
      >
        <CtaLink to="/contact" variant="outline" className="h-12">
          Ask about availability
        </CtaLink>
      </PageHeader>

      <Section tone="paper" labelledBy="large-pink-heading">
        <div className="shell">
          <SectionHeading
            id="large-pink-heading"
            eyebrow="Larger pink porcelain roses"
            title="Blanks with embedded larger pink porcelain roses."
            lede={`A larger pink porcelain rose is embedded in the upper clear portion, with a solid color lower portion. Each blank is $14.95. ${SHIPPING}`}
          />
          <BlankGrid items={largePink} />
        </div>
      </Section>

      <Section labelledBy="small-blue-heading">
        <div className="shell">
          <SectionHeading
            id="small-blue-heading"
            eyebrow="Smaller blue porcelain roses"
            title="Blanks with embedded smaller blue porcelain roses."
            lede={`A smaller blue porcelain rose sits in the clear upper portion above a solid color base. Each blank is $14.95. ${SHIPPING}`}
          />
          <BlankGrid items={smallBlue} />
        </div>
      </Section>

      <Section tone="paper" labelledBy="red-paper-heading">
        <div className="shell">
          <SectionHeading
            id="red-paper-heading"
            eyebrow="Red paper roses"
            title="Blanks with embedded red paper roses."
            lede={`A red paper rose is embedded in the clear upper portion above a solid color base. Each blank is $14.95. ${SHIPPING}`}
          />
          <BlankGrid items={redPaper} />
        </div>
      </Section>

      <Section labelledBy="white-paper-heading">
        <div className="shell">
          <SectionHeading
            id="white-paper-heading"
            eyebrow="White paper roses"
            title="Blanks with embedded white paper roses."
            lede={`A white paper rose is embedded in the clear upper portion above a solid color base. Each blank is $14.95. ${SHIPPING}`}
          />
          <BlankGrid items={whitePaper} />
        </div>
      </Section>

      <Section tone="paper" labelledBy="embedded-fly-heading">
        <div className="shell">
          <SectionHeading
            id="embedded-fly-heading"
            eyebrow="Embedded fishing flies"
            title="Blanks with embedded fishing flies."
            lede={`A fishing fly is embedded at the bottom of the clear upper portion. Each blank is $14.95. ${SHIPPING}`}
          />
          <BlankGrid items={embeddedFly} />
        </div>
      </Section>

      <Section labelledBy="suspended-fly-heading">
        <div className="shell">
          <SectionHeading
            id="suspended-fly-heading"
            eyebrow="Suspended fishing flies"
            title="Blanks with suspended fishing flies."
            lede={`The fly floats mid-cast in the clear upper portion, so it reads in the round once turned. Each blank is $16.95. ${SHIPPING}`}
          />
          <BlankGrid items={suspendedFly} />
          <p className="mt-8 max-w-2xl text-sm text-muted-foreground">
            Turning a batch for a club, a wedding or a fundraiser? Tell Paul how many you need and
            he will quote it with the right shipping tier applied.
          </p>
        </div>
      </Section>

      <FinalCTA
        heading="Want your own object cast into a stopper blank?"
        body="Send Paul a fly, a keepsake or a color to match and he will cast the blanks for you."
        label="Start a conversation"
      />
    </>
  );
}
